<script setup>
import { computed, ref, watch } from 'vue'

/**
 * Avatar de conta: mostra a foto quando existe e, sem ela, a inicial do nome.
 *
 * Substitui o `default_avatar.png`, que era a mesma silhueta cinza para todo
 * mundo — numa lista de contas isso deixava as linhas visualmente iguais.
 *
 * O tamanho vem de fora (`size-12`, `size-14`…), porque muda em cada lugar; a
 * forma é prop porque o design usa quadrado no header e em "Minhas contas" e
 * círculo no seletor de contas do header.
 */
const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  shape: {
    type: String,
    default: 'square',
    validator: value => ['square', 'circle'].includes(value),
  },
})

// URL preenchida mas quebrada é comum aqui (foto removida do storage, caminho
// relativo servido por outro host). Sem isto o `<img>` fica no lugar exibindo o
// `alt`, que é justamente o nome da conta transbordando de dentro do avatar.
const failed = ref(false)
watch(
  () => props.src,
  () => {
    failed.value = false
  },
)

const showPhoto = computed(() => !!props.src && !failed.value)

// Defensivo: a prop é String, mas um valor de outro tipo aqui derrubava o render
// inteiro do componente pai em vez de degradar para o avatar sem inicial.
const initial = computed(() => {
  const name = typeof props.name === 'string' ? props.name : ''

  return name.trim().charAt(0).toUpperCase()
})
</script>

<template>
  <div
    class="bg-neutral-20 flex shrink-0 items-center justify-center overflow-hidden text-lg font-medium text-neutral-50 select-none"
    :class="shape === 'circle' ? 'rounded-full' : 'rounded-xl'"
  >
    <!-- `alt` vazio de propósito: o nome da conta aparece ao lado em todos os
         usos, e um alt preenchido vaza como texto enquanto a imagem carrega. -->
    <img v-if="showPhoto" :src="src" alt="" class="size-full object-cover" @error="failed = true" />
    <span v-else aria-hidden="true">{{ initial }}</span>
  </div>
</template>
